/**
 * Minecraft Java Edition 1.8.8 Food and Hunger Mechanics.
 *
 * Implements the complete FoodStats logic from Minecraft 1.8.8 (`net.minecraft.util.FoodStats`):
 * - Food level: 0..20 (10 drumsticks / shanks on HUD).
 * - Food saturation: 0..foodLevel (buffer depleted before food level drops, starts at 5.0).
 * - Food exhaustion: 0..40 (accumulated through movement, jumping, mining, attacks, and healing).
 * - Rollover: Every 4.0 exhaustion consumes 1 point of saturation, or 1 point of food if saturation is 0.
 * - Natural regeneration: When foodLevel >= 18 and hurt, heals 1 HP (0.5 heart) every 4.0s (80 ticks) and adds 3.0 exhaustion.
 * - Starvation: When foodLevel <= 0, damages 1 HP every 4.0s (80 ticks) down to 1 HP on Normal.
 * - Sprint gating: Sprinting is disabled when foodLevel <= 6 (3 shanks).
 */

export const MAX_FOOD = 20;
export const DEFAULT_SATURATION = 5.0;
export const SPRINT_MIN_FOOD = 6;
export const EXHAUSTION_PER_REGEN = 3.0;
export const EXHAUSTION_ROLLOVER = 4.0;
export const FOOD_TICK_INTERVAL = 4.0; // 80 game ticks = 4.0 seconds

export interface FoodPlayerLike {
  health: number;
  heal(hp: number): number;
  hurt(dmg: number, cause: string): void;
}

export class FoodStats {
  foodLevel = MAX_FOOD;
  foodSaturationLevel = DEFAULT_SATURATION;
  foodExhaustionLevel = 0.0;
  foodTimer = 0.0;
  prevFoodLevel = MAX_FOOD;

  /** Add food points and saturation from eating an item. */
  addStats(foodLevelIn: number, foodSaturationModifier: number): void {
    this.foodLevel = Math.min(this.foodLevel + foodLevelIn, MAX_FOOD);
    this.foodSaturationLevel = Math.min(
      this.foodSaturationLevel + foodLevelIn * foodSaturationModifier * 2.0,
      this.foodLevel,
    );
  }

  /** Accumulate exhaustion from physical exertion. */
  addExhaustion(amount: number): void {
    this.foodExhaustionLevel = Math.min(this.foodExhaustionLevel + amount, 40.0);
  }

  /** Player can eat only when hungry (< 20), unless item is marked alwaysEdible. */
  needFood(): boolean {
    return this.foodLevel < MAX_FOOD;
  }

  /** Sprinting requires more than 6 food points (> 3 full shanks). */
  canSprint(): boolean {
    return this.foodLevel > SPRINT_MIN_FOOD;
  }

  /** Reset food stats on respawn. */
  reset(): void {
    this.foodLevel = MAX_FOOD;
    this.foodSaturationLevel = DEFAULT_SATURATION;
    this.foodExhaustionLevel = 0.0;
    this.foodTimer = 0.0;
    this.prevFoodLevel = MAX_FOOD;
  }

  /**
   * Tick food logic in survival mode.
   * Handles exhaustion rollover, natural health regeneration, and starvation damage.
   */
  update(survival: boolean, dt: number, player: FoodPlayerLike, sinceDamage = Infinity): void {
    if (!survival) return;

    this.prevFoodLevel = this.foodLevel;

    // Rollover exhaustion: 4 exhaustion drains 1 point of saturation, then food level
    while (this.foodExhaustionLevel >= EXHAUSTION_ROLLOVER) {
      this.foodExhaustionLevel -= EXHAUSTION_ROLLOVER;
      if (this.foodSaturationLevel > 0.0) {
        this.foodSaturationLevel = Math.max(0.0, this.foodSaturationLevel - 1.0);
      } else {
        this.foodLevel = Math.max(0, this.foodLevel - 1);
      }
    }

    // Natural regeneration: when foodLevel >= 18 and hurt, heal 1 HP every 80 ticks (4.0s)
    // Respects Minecraft's recovery quiet delay (sinceDamage >= 8s)
    const shouldHeal = player.health > 0 && player.health < 20 && sinceDamage >= 8;
    if (this.foodLevel >= 18 && shouldHeal) {
      this.foodTimer += dt;
      if (this.foodTimer >= FOOD_TICK_INTERVAL) {
        this.foodTimer = 0.0;
        player.heal(1);
        this.addExhaustion(EXHAUSTION_PER_REGEN);
      }
    } else if (this.foodLevel <= 0) {
      this.foodTimer += dt;
      if (this.foodTimer >= FOOD_TICK_INTERVAL) {
        this.foodTimer = 0.0;
        // In Normal difficulty, starvation stops at 1 HP (half heart)
        if (player.health > 1) {
          player.hurt(1, "starved");
        }
      }
    } else {
      this.foodTimer = 0.0;
    }
  }
}
